import React from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockChip from '../components/GuideMockChip';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideContractSearch: React.FC = () => {
  return (
    <GuidePageLayout
      title="契約・契約申請を検索する"
      description="契約一覧と契約申請一覧の検索、契約詳細と履歴の確認方法です。"
      breadcrumbs={[{ label: '契約管理', path: '/guide/contract/overview' }]}
    >
      {/* 契約検索 */}
      <GuideSection title="契約を検索する（BUC-CT09）">
        <GuideStepList
          steps={[
            { label: '契約管理メニューから契約一覧画面を開く' },
            {
              label: '検索条件を入力する',
              content: (
                <GuideMockContainer label="検索条件チップ（契約一覧）">
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <GuideMockChip label="管理ID: CONT-001" color="info" />
                    <GuideMockChip label="プロジェクト: プロジェクトA" color="warning" />
                    <GuideMockChip label="課長: 山田太郎" color="success" />
                    <GuideMockChip label="契約終了を含む" color="default" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            { label: '「検索」ボタンを押下する' },
            { label: '検索結果が管理ID降順で一覧表示される' },
          ]}
        />
        <GuideTable
          headers={['項目名', '形式', '備考']}
          rows={[
            ['管理ID', 'テキスト', '完全一致'],
            ['担当者', 'プルダウン', 'ユーザーから選択'],
            ['課長', 'プルダウン', 'ユーザーから選択'],
            ['プロジェクト', 'プルダウン', ''],
            ['メンバー', 'プルダウン', ''],
            ['パートナー', 'プルダウン', ''],
            ['取引先', 'プルダウン', ''],
            ['契約形態', 'プルダウン', 'マスタ区分から選択'],
            ['年度', 'テキスト', '契約期間が含まれる年度'],
            ['年度月', 'テキスト', '契約期間が含まれる年度月'],
            ['契約終了を含む', 'チェックボックス', 'ONで解除済みも表示'],
          ]}
        />
        <GuideTable
          headers={['表示項目', '位置', '説明']}
          rows={[
            ['管理ID', 'メイン（左）', '契約の管理ID'],
            ['申請中Chip', 'メイン（左・IDの横）', '進行中の申請がある場合に表示'],
            ['契約ステータスChip', 'メイン（右）', '契約中 or 契約終了を表示'],
            [
              'プロジェクト名 / メンバー名',
              'サブテキスト（1行目）',
              'プロジェクトとメンバーの名前',
            ],
            ['契約期間', 'サブテキスト（2行目）', '契約開始日 〜 契約終了日'],
          ]}
        />
        <GuideMockContainer label="契約リスト項目の表示例">
          <Stack spacing={1.5}>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography variant="body2" fontWeight={600}>
                    CONT-001
                  </Typography>
                  <GuideMockChip label="申請中" color="warning" variant="outlined" size="small" />
                </Stack>
                <GuideMockChip label="契約中" color="success" size="small" />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                プロジェクトA / 田中太郎
              </Typography>
              <Typography variant="caption" color="text.secondary">
                2025/04/01 〜 2026/03/31
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" fontWeight={600}>
                  CONT-002
                </Typography>
                <GuideMockChip label="契約終了" color="default" size="small" />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                プロジェクトB / 佐藤花子
              </Typography>
              <Typography variant="caption" color="text.secondary">
                2024/04/01 〜 2025/03/31
              </Typography>
            </Stack>
          </Stack>
        </GuideMockContainer>
        <GuideTable
          headers={['Chip', '意味', '表示条件', '色']}
          rows={[
            [
              '申請中',
              '進行中の契約申請あり',
              '未完了の申請が紐づいている場合',
              '黄枠（warning, outlined）',
            ],
            ['契約中', '契約が有効', 'ステータスが ACTIVE の場合', '緑（success）'],
            ['契約終了', '契約が終了', 'ステータスが TERMINATED の場合', 'グレー（default）'],
          ]}
        />
      </GuideSection>

      {/* 契約詳細 */}
      <GuideSection title="契約の詳細と履歴を確認する（BUC-CT08）">
        <GuideStepList
          steps={[
            { label: '契約一覧画面で対象契約を選択する' },
            { label: '契約詳細が表示される' },
            {
              label: '進行中の申請がある場合は現在値と申請値が並行表示される',
            },
            {
              label: '履歴を確認する場合は「前へ」「次へ」で過去バージョンに遷移する',
            },
          ]}
        />
        <GuideInfoBox>下部のタブでコメントと申請一覧を確認できます。</GuideInfoBox>
      </GuideSection>

      {/* 契約申請検索 */}
      <GuideSection title="契約申請を検索する（BUC-CT10）">
        <GuideStepList
          steps={[
            { label: '契約管理メニューから申請一覧画面を開く' },
            {
              label: '検索条件を入力する',
              content: (
                <GuideMockContainer label="検索条件チップ（申請一覧）">
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <GuideMockChip label="申請種別: 新規申請" color="secondary" />
                    <GuideMockChip label="ステータス: 申請中" color="success" />
                    <GuideMockChip label="原籍確認シート必要" color="default" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            { label: '「検索」ボタンを押下する' },
          ]}
        />
        <GuideTable
          headers={['項目名', '形式', '備考']}
          rows={[
            ['管理ID', 'テキスト', ''],
            ['申請種別', 'プルダウン', '新規 / 変更 / 解除'],
            ['ステータス', 'プルダウン', '調整中 / 申請中 / 対応中 / 承認 / 否決 / 取消'],
            ['担当者 / 課長', 'プルダウン', 'ユーザーから選択'],
            ['プロジェクト / メンバー', 'プルダウン', ''],
            ['パートナー / 取引先 / 承認者', 'プルダウン', ''],
            ['申請日(From/To)', '日付', '期間指定'],
            ['承認日(From/To)', '日付', '期間指定'],
            ['原籍確認シート必要', 'チェックボックス', ''],
          ]}
        />
        <GuideTable
          headers={['表示項目', '位置', '説明']}
          rows={[
            ['管理ID', 'メイン（左）', '申請の管理ID'],
            ['申請種別Chip', 'メイン（左・IDの横）', '新規申請 / 変更申請 / 解除申請'],
            ['ステータスChip', 'メイン（左・種別の横）', '申請の現在ステータス'],
            [
              '担当者 / 課長 / パートナー / プロジェクト',
              'サブテキスト（1行目）',
              '関連情報をスラッシュ区切りで表示',
            ],
            ['申請日 / 承認情報', 'サブテキスト（2行目）', '申請日と承認者・承認日'],
          ]}
        />
        <GuideMockContainer label="契約申請リスト項目の表示例">
          <Stack spacing={1.5}>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="body2" fontWeight={500}>
                  REQ-001
                </Typography>
                <GuideMockChip label="新規申請" color="primary" size="small" />
                <GuideMockChip label="申請中" color="info" size="small" />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                田中太郎 / 株式会社サンプル / プロジェクトA
              </Typography>
              <Typography variant="caption" color="text.secondary">
                申請日: 2025/06/01 / 承認: —
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="body2" fontWeight={500}>
                  REQ-002
                </Typography>
                <GuideMockChip label="変更申請" color="warning" size="small" />
                <GuideMockChip label="承認" color="success" size="small" />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                佐藤花子 / 山田太郎 / プロジェクトB
              </Typography>
              <Typography variant="caption" color="text.secondary">
                申請日: 2025/05/15 / 承認: 鈴木一郎 2025/05/20
              </Typography>
            </Stack>
          </Stack>
        </GuideMockContainer>
        <GuideTable
          headers={['Chip', '意味', '表示条件', '色']}
          rows={[
            ['新規申請', '新規契約の申請', '申請種別が CREATE の場合', '青'],
            ['変更申請', '既存契約の変更申請', '申請種別が CHANGE の場合', 'オレンジ'],
            ['解除申請', '契約解除の申請', '申請種別が TERMINATE の場合', '赤'],
            ['調整中', '申請前の調整段階', 'ステータスが ADJUSTING の場合', 'グレー'],
            ['申請中', '申請済み・対応待ち', 'ステータスが SUBMITTED の場合', '青'],
            ['対応中', '管理部門が対応中', 'ステータスが IN_PROGRESS の場合', 'オレンジ'],
            ['承認', '申請が承認された', 'ステータスが APPROVED の場合', '緑'],
            ['否決', '申請が否決された', 'ステータスが REJECTED の場合', '赤'],
            ['取消', '申請が取り消された', 'ステータスが CANCELLED の場合', 'グレー'],
          ]}
        />
        <GuideInfoBox>
          管理部門の場合、初期状態でステータス「申請中」がセットされます。
        </GuideInfoBox>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideContractSearch;
